import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Stack, Divider, Paper,
  TextField, InputAdornment, IconButton, Chip, CircularProgress, Collapse, Badge,
} from '@mui/material';
import {
  ArrowBack, Delete, CurrencyRupee,
  CheckCircle, Print, Stars, Person,
  LocalAtm, CreditCard, AccountBalanceWallet, QrCode2, Payment,
  LocalOfferOutlined, CardGiftcard,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { usePrinterContext, browserPrintOrder } from '../../context/PrinterContext';
import { useGetPaymentModesQuery } from '../../services/paymentModeApi';
import { usePayOrderMutation, useGetOrderQuery } from '../../services/orderApi';
import { useToast } from '../common/ToastProvider';
import { customerApi, useGetCustomerQuery } from '../../services/customerApi';
import { mapOrderToReceipt } from './receipt/mapOrderToReceipt';
import { useGetReceiptTemplateQuery } from '../../services/receiptTemplateApi';
import CouponSection from './checkout/CouponSection';
import CheckoutLoyaltyPanel from './checkout/CheckoutLoyaltyPanel';
import SuccessOverlay from './checkout/PaymentSuccessOverlay';
import BillUpdateConfirmDialog from './BillUpdateConfirmDialog';
import GiftVoucherLookupDialog from './giftVoucher/GiftVoucherLookupDialog';
import { isGiftVoucherMode } from './giftVoucher/gvUtils';
import { setCurrentOrder, setAppliedPromotions } from '../../store/orderSlice';
import { useStackingTraceToasts } from '../../hooks/useStackingTraceToasts';
import { useWalletOtpVerification } from '../../hooks/useWalletOtpVerification';
import CustomerWalletOtpModal from './checkout/CustomerWalletOtpModal';
import UntaggedIncentiveStrip from './incentives/UntaggedIncentiveStrip';

const fmt = (v) =>
  `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPaymentAmount = (value) => (
  Number(value || 0).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
);

const getMaxStoreCreditAmount = ({ total, storeCreditBalance, payments = [], storeCreditModeId = null }) => {
  const otherPaid = payments.reduce((sum, payment) => (
    payment.payment_mode_id === storeCreditModeId
      ? sum
      : sum + (parseFloat(payment.amount) || 0)
  ), 0);

  return Math.min(storeCreditBalance, Math.max(0, total - otherPaid));
};

const isStoreCreditMode = (mode = {}) => {
  const haystack = `${mode.name || ''} ${mode.code || ''}`.toLowerCase();
  return haystack.includes('store credit') || haystack.includes('store_credit');
};

const isCashMode = (mode = {}) => {
  const haystack = `${mode.name || ''} ${mode.code || ''}`.toLowerCase();
  return haystack.includes('cash');
};

const getModeIcon = (name = '', color = '#94A3B8', small = false) => {
  const n = name.toLowerCase();
  const sx = { fontSize: small ? 14 : 16, color };
  if (n.includes('cash'))   return <LocalAtm sx={sx} />;
  if (n.includes('card'))   return <CreditCard sx={sx} />;
  if (n.includes('upi'))    return <QrCode2 sx={sx} />;
  if (n.includes('wallet')) return <AccountBalanceWallet sx={sx} />;
  if (n.includes('gift')) return <CardGiftcard sx={sx} />;
  return <Payment sx={sx} />;
};

/* ── Single billing breakdown row ── */
const BRow = ({ label, value, bold, green, negate, topDivider }) => {
  const num = parseFloat(value || 0);
  return (
    <>
      {topDivider && <Divider sx={{ my: 0.5 }} />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: bold ? 0.3 : 0.15 }}>
        <Typography sx={{ fontSize: bold ? '0.82rem' : '0.76rem', color: bold ? '#1E293B' : '#64748B', fontWeight: bold ? 700 : 400 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: bold ? '1.02rem' : '0.82rem', fontWeight: bold ? 900 : 600, color: green ? '#16A34A' : bold ? '#4F46E5' : '#334155' }}>
          {negate ? `−${fmt(num)}` : fmt(num)}
        </Typography>
      </Box>
    </>
  );
};

/* ── Apply-coupon trigger (desktop header / mobile billing heading) ── */
const CouponTriggerButton = ({ open, onToggle, applied, mobile }) => (
  <Badge color="success" variant="dot" overlap="circular" invisible={!applied}>
    <Button
      size="small"
      onClick={onToggle}
      startIcon={<LocalOfferOutlined sx={{ fontSize: '14px !important' }} />}
      sx={{
        minWidth: 0, flexShrink: 0, px: mobile ? 0.85 : 1.25, py: 0.35, borderRadius: '8px',
        textTransform: 'none', fontWeight: 700, fontSize: mobile ? '0.66rem' : '0.72rem',
        color: open || applied ? '#4F46E5' : '#64748B',
        border: '1px solid', borderColor: open || applied ? '#C7D2FE' : '#E2E8F0',
        bgcolor: open || applied ? '#EEF2FF' : '#FFF',
        '&:hover': { bgcolor: '#EEF2FF', borderColor: '#C7D2FE' },
        '& .MuiButton-startIcon': { mr: 0.4 },
      }}
    >
      {mobile ? '+ Coupon' : 'Apply Coupon'}
    </Button>
  </Badge>
);

/* ── Billing details card ── */
const BillingCard = ({ gross_subtotal, subtotal, discount_total, coupon_discount_amount, tax_total, total, rounding_adjustment, isIntrastate, compact, loyaltyRedemption, netPayable, headerAction, couponSlot }) => {
  const loyaltyAmt = parseFloat(loyaltyRedemption || 0);
  const roundingNum = parseFloat(rounding_adjustment || 0);
  return (
    <Box sx={{ bgcolor: compact ? '#F8FAFC' : 'transparent', border: compact ? '1px solid #F1F5F9' : 'none', borderRadius: '10px', px: compact ? 1.5 : 0, py: compact ? 1 : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, minHeight: 24 }}>
        <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.07em' }}>
          BILLING DETAILS
        </Typography>
        {headerAction}
      </Box>
      {couponSlot}
      <BRow label="Subtotal" value={gross_subtotal} />
      {(() => {
        const couponAmt      = parseFloat(coupon_discount_amount || 0);
        const nonCouponDisc  = parseFloat(discount_total || 0) - couponAmt;
        return (
          <>
            {nonCouponDisc > 0.001 && <BRow label="Discount" value={nonCouponDisc} green negate />}
            {couponAmt > 0 && (
              <BRow label="Coupon" value={couponAmt} green negate />
            )}
          </>
        );
      })()}
      <BRow label="Taxable Amount" value={subtotal} />
      {isIntrastate ? (
        <>
          <BRow label="CGST" value={parseFloat(tax_total) / 2} />
          <BRow label="SGST" value={parseFloat(tax_total) / 2} />
        </>
      ) : (
        parseFloat(tax_total) > 0 && <BRow label="IGST" value={tax_total} />
      )}
      {loyaltyAmt > 0 ? (
        /* With loyalty: rounding now snaps the NET to a whole rupee (PR #179),
           so show it after the redemption and lead to a single bold total. */
        <>
          <BRow label="Loyalty Redeemed" value={loyaltyAmt} green negate />
          {roundingNum !== 0 && (
            <BRow label="Rounding" value={Math.abs(roundingNum)} negate={roundingNum < 0} />
          )}
          <BRow label="Net Payable" value={netPayable} bold topDivider />
        </>
      ) : (
        <>
          {roundingNum !== 0 && (
            <BRow label="Rounding" value={Math.abs(roundingNum)} negate={roundingNum < 0} />
          )}
          <BRow label="Total Due" value={total} bold topDivider />
        </>
      )}
    </Box>
  );
};

/* ── Order items list ── */
const ItemsList = ({ items }) => (
  <>
    {items.length === 0 ? (
      <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', py: 1.5 }}>No items</Typography>
    ) : items.map((item, idx) => (
      <Box key={item.order_item_id || idx} sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 1, borderBottom: idx < items.length - 1 ? '1px solid #F8FAFC' : 'none',
      }}>
        <Box sx={{ minWidth: 0, mr: 1.5 }}>
          <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.product_name}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: 0.1 }}>
            {fmt(item.unit_price)} × {item.quantity}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1E293B', flexShrink: 0 }}>
          {fmt(item.line_total)}
        </Typography>
      </Box>
    ))}
  </>
);

/* ═══════════════════════════════════════════════════
   CheckoutPage
═══════════════════════════════════════════════════ */
const CheckoutPage = ({ open, onClose, onNewSale, onPaymentCompleted, cart = [], onTagSalesperson }) => {
  const showToast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isConnected, status: printerStatus, silentPrintOrder } = usePrinterContext();

  useStackingTraceToasts();

  const {
    currentOrderId, orderNumber, referenceNumber,
    grand_total, tax_total, discount_total, gross_subtotal, subtotal, rounding_adjustment,
    selectedCustomer, cartItems: orderCartItems,
    coupon_code, coupon_discount_amount,
    loyalty_redemption_amount,
  } = useSelector((state) => state.order);
  const { organization } = useSelector((state) => state.auth);

  const walletOtpEnabled = organization?.customer_wallet_otp_enabled === true;
  const walletOtpMode = organization?.customer_wallet_otp_mode || 'separate';
  const scWallet = useWalletOtpVerification({ orderId: currentOrderId, walletType: 'store_credit' });
  const lpWallet = useWalletOtpVerification({ orderId: currentOrderId, walletType: 'loyalty_points' });

  const [payments, setPayments] = useState([]);
  // Gift vouchers are separate tenders: several vouchers can share the one GIFT_VOUCHER mode,
  // each carrying the short-lived token from /gift_vouchers/lookup.
  const [gvPayments, setGvPayments] = useState([]);
  const [gvDialogOpen, setGvDialogOpen] = useState(false);
  const [billUpdate, setBillUpdate] = useState(null);
  const [paidOrder, setPaidOrder] = useState(null);
  const [paidCustomerWhatsappOptIn, setPaidCustomerWhatsappOptIn] = useState(null);
  const [hasInitializedPayments, setHasInitializedPayments] = useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const paymentTileRefs = useRef([]);
  const amountFieldRefs = useRef([]);

  const { data: modesData, isLoading: loadingModes } = useGetPaymentModesQuery(undefined, { skip: !open });
  const { data: customerDetailRes, isFetching: isCustomerDetailLoading } = useGetCustomerQuery(selectedCustomer?.id, {
    skip: !open || !selectedCustomer?.id,
  });
  const [payOrder, { isLoading: isPaying }] = usePayOrderMutation();
  const { data: receiptOrderPayload, isFetching: isReceiptLoading } = useGetOrderQuery(paidOrder?.id, {
    skip: !paidOrder?.id,
  });

  // The gift voucher tender only shows while the org has gift vouchers on for POS.
  const posGiftVouchersEnabled = organization?.pos_gift_vouchers_enabled === true;
  const modes = useMemo(() => {
    const all = modesData?.data?.payment_modes || (Array.isArray(modesData?.data) ? modesData.data : []);
    return posGiftVouchersEnabled ? all : all.filter((mode) => !isGiftVoucherMode(mode));
  }, [modesData, posGiftVouchersEnabled]);
  const storeCreditMode = useMemo(() => modes.find((mode) => isStoreCreditMode(mode)), [modes]);
  const giftVoucherMode = useMemo(() => modes.find((mode) => isGiftVoucherMode(mode)), [modes]);
  const total = parseFloat(grand_total) || 0;
  // Loyalty redemption is a separate tender: grand_total is unchanged; the cash
  // the cashier collects is the net payable. All payment math uses `payable`.
  const redemptionAmount = parseFloat(loyalty_redemption_amount || 0);
  const payable = Math.max(0, total - redemptionAmount);
  const customerDetail = customerDetailRes?.data?.customer || customerDetailRes?.data || selectedCustomer;
  const storeCreditBalance = parseFloat(customerDetail?.credit_balance || 0);
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const paidReceipt = useMemo(
    () => mapOrderToReceipt(receiptOrderPayload, receiptTemplate),
    [receiptOrderPayload, receiptTemplate]
  );

  const items = useMemo(() => {
    if (cart.length === 0) return orderCartItems || [];
    return cart.map(cartItem => {
      const srv = (orderCartItems || []).find(oi => oi.product_id === cartItem.id);
      return {
        order_item_id: srv?.order_item_id ?? cartItem.id,
        product_name:  cartItem.name,
        quantity:      cartItem.qty ?? 1,
        unit_price:    srv?.unit_price  ?? cartItem.retail_price ?? 0,
        line_total:    srv?.line_total  ?? (parseFloat(cartItem.retail_price || 0) * (parseInt(cartItem.qty) || 1)),
        cgst_amount:   srv?.cgst_amount ?? 0,
      };
    });
  }, [cart, orderCartItems]);

  useEffect(() => {
    if (open) {
      setPayments([]);
      setGvPayments([]);
      setGvDialogOpen(false);
      setPaidOrder(null);
      setPaidCustomerWhatsappOptIn(null);
      setHasInitializedPayments(false);
      setLoyaltyOpen(false);
      setCouponOpen(false);
      setBillUpdate(null);
      scWallet.reset();
      lpWallet.reset();
    }
  }, [open]);

  // Loyalty panel auto-opens when a redemption is held (on checkout open or when
  // applied mid-session); the cashier can collapse it manually with the ✕.
  useEffect(() => { if (open && redemptionAmount > 0) setLoyaltyOpen(true); }, [open, redemptionAmount]);

  const gvPaid = useMemo(() => gvPayments.reduce((sum, g) => sum + g.amount, 0), [gvPayments]);
  const totalPaid = useMemo(
    () => payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) + gvPaid,
    [payments, gvPaid]
  );
  const remaining = Math.max(0, payable - totalPaid);
  const changeDue = totalPaid > payable ? totalPaid - payable : 0;
  const canPay    = (payments.length > 0 || gvPayments.length > 0) && totalPaid >= payable && payments.every(p => p.payment_mode_id && p.amount !== '');

  const isIntrastate = useMemo(
    () => items.some(item => parseFloat(item.cgst_amount || 0) > 0),
    [items]
  );

  /* Tap a chip → add row with blank amount; tap again → remove.
     Store Credit, when the org has customer_wallet_otp_enabled, is gated:
     the row isn't added until the customer verifies an OTP — see the
     scWallet branch below and the effect that adds the row once verified. */
  const toggleMode = (modeId) => {
    const mode = modes.find((entry) => entry.id === modeId);
    if (isGiftVoucherMode(mode)) {
      if (remaining <= 0) { showToast('The bill is already fully paid', 'info'); return; }
      setGvDialogOpen(true);
      return;
    }
    if (isStoreCreditMode(mode) && !selectedCustomer?.id) return;

    if (isStoreCreditMode(mode) && walletOtpEnabled) {
      const isSelected = payments.some(p => p.payment_mode_id === modeId);
      if (isSelected) {
        setPayments(prev => prev.filter(p => p.payment_mode_id !== modeId));
        scWallet.reset();
        return;
      }
      if (scWallet.status === 'verified') {
        const amount = formatPaymentAmount(getMaxStoreCreditAmount({ total: payable, storeCreditBalance, payments, storeCreditModeId: modeId }));
        setPayments(prev => [...prev, { payment_mode_id: modeId, amount: amount === '0' ? '' : amount }]);
        return;
      }
      if (scWallet.status === 'pending') { scWallet.openModal(); return; }
      if (scWallet.status === 'blocked_phone' || scWallet.status === 'blocked_connector') return;
      if (walletOtpMode === 'combined' && lpWallet.status === 'verified') {
        scWallet.markVerifiedInstantly();
        showToast('Already verified for this order — applied instantly, no second code needed', 'success');
        return;
      }
      scWallet.start();
      return;
    }

    setPayments(prev => {
      const exists = prev.find(p => p.payment_mode_id === modeId);
      if (exists) return prev.filter(p => p.payment_mode_id !== modeId);
      const amount = isStoreCreditMode(mode)
        ? formatPaymentAmount(getMaxStoreCreditAmount({ total: payable, storeCreditBalance, payments: prev, storeCreditModeId: modeId }))
        : '';

      return [...prev, { payment_mode_id: modeId, amount: amount === '0' ? '' : amount }];
    });
  };

  /* Roving focus across the payment-mode tile grid (3 columns) */
  const handlePaymentTileKeyDown = (e, idx, mode, disabled) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) toggleMode(mode.id);
      return;
    }
    const cols = 3;
    let nextIdx = null;
    if (e.key === 'ArrowRight') nextIdx = idx + 1;
    else if (e.key === 'ArrowLeft') nextIdx = idx - 1;
    else if (e.key === 'ArrowDown') nextIdx = idx + cols;
    else if (e.key === 'ArrowUp') nextIdx = idx - cols;
    if (nextIdx !== null && nextIdx >= 0 && nextIdx < modes.length) {
      e.preventDefault();
      paymentTileRefs.current[nextIdx]?.focus();
    }
  };

  const applyAmountToPaymentMode = (modeId, nextAmount) => {
    const mode = modes.find((entry) => entry.id === modeId);
    const normalizedValue = `${nextAmount ?? ''}`;
    const numericValue = parseFloat(normalizedValue);
    const maxStoreCreditAmount = getMaxStoreCreditAmount({ total: payable, storeCreditBalance, payments, storeCreditModeId: modeId });
    const cappedValue = isStoreCreditMode(mode) && normalizedValue !== ''
      ? formatPaymentAmount(Math.min(numericValue || 0, maxStoreCreditAmount))
      : normalizedValue;

    setPayments(prev => prev.map((payment) => (
      payment.payment_mode_id === modeId
        ? { ...payment, amount: cappedValue }
        : payment
    )));
  };

  const updateAmount = (modeId, value) => {
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    applyAmountToPaymentMode(modeId, value);
  };

  useEffect(() => {
    if (!open || hasInitializedPayments || !modes.length) return;

    // Cashier must explicitly pick a payment method — no default selection.
    setHasInitializedPayments(true);
  }, [hasInitializedPayments, modes, open]);

  useEffect(() => {
    if (!payments.length || !storeCreditMode) return;

    const storeCreditPayment = payments.find((payment) => payment.payment_mode_id === storeCreditMode.id);
    if (!storeCreditPayment || storeCreditPayment.amount !== '') return;

    const eligibleAmount = getMaxStoreCreditAmount({
      total: payable,
      storeCreditBalance,
      payments,
      storeCreditModeId: storeCreditMode.id,
    });
    if (eligibleAmount <= 0) return;

    setPayments((prev) => prev.map((payment) => (
      payment.payment_mode_id === storeCreditMode.id
        ? { ...payment, amount: formatPaymentAmount(eligibleAmount) }
        : payment
    )));
  }, [storeCreditMode, payments, storeCreditBalance, payable]);

  // Store Credit row is added the moment its wallet OTP verifies (idle → sending →
  // pending happen without a payments row at all — see toggleMode above).
  useEffect(() => {
    if (!walletOtpEnabled || !storeCreditMode) return;
    if (scWallet.status !== 'verified') return;
    if (payments.some((p) => p.payment_mode_id === storeCreditMode.id)) return;

    const amount = formatPaymentAmount(getMaxStoreCreditAmount({
      total: payable, storeCreditBalance, payments, storeCreditModeId: storeCreditMode.id,
    }));
    setPayments((prev) => [...prev, { payment_mode_id: storeCreditMode.id, amount: amount === '0' ? '' : amount }]);
  }, [scWallet.status, walletOtpEnabled, storeCreditMode]);

  // Order total changed after Store Credit was verified — that consent no
  // longer applies to the new amount, so re-verification is required.
  const prevGrandTotalRef = useRef(grand_total);
  useEffect(() => {
    if (prevGrandTotalRef.current !== grand_total) {
      if (walletOtpEnabled && scWallet.status === 'verified' && storeCreditMode) {
        setPayments((prev) => prev.filter((p) => p.payment_mode_id !== storeCreditMode.id));
        scWallet.invalidate();
        showToast('Order changed — Store Credit needs re-verification', 'error');
      }
      prevGrandTotalRef.current = grand_total;
    }
  }, [grand_total]);

  useEffect(() => {
    if (!open) return undefined;

    const handleCheckoutHotkey = (e) => {
      if (e.key !== 'F10') return;
      if (paidOrder) return;
      e.preventDefault();

      const targetPayment = payments[payments.length - 1];
      if (!targetPayment || remaining <= 0) return;

      const currentAmount = parseFloat(targetPayment.amount) || 0;
      applyAmountToPaymentMode(targetPayment.payment_mode_id, formatPaymentAmount(currentAmount + remaining));
    };

    window.addEventListener('keydown', handleCheckoutHotkey);
    return () => window.removeEventListener('keydown', handleCheckoutHotkey);
  }, [applyAmountToPaymentMode, open, paidOrder, payments, remaining]);

  const handleSubmit = async () => {
    if (!currentOrderId) return;
    try {
      const res = await payOrder({
        id: currentOrderId,
        payments: [
          ...payments.map(p => ({
            payment_mode_id: p.payment_mode_id,
            amount: parseFloat(p.amount || 0).toFixed(2),
          })),
          ...gvPayments.map(g => ({
            payment_mode_id: giftVoucherMode?.id,
            amount: g.amount.toFixed(2),
            gift_voucher_token: g.token,
          })),
        ],
      }).unwrap();
      const capturedOptIn = customerDetail?.whatsapp_opt_in ?? null;
      setPaidOrder(res.data?.order || res.data);
      setPaidCustomerWhatsappOptIn(capturedOptIn);
      onPaymentCompleted?.();

      // Store-credit payments change customer balance on backend.
      // Invalidate customer cache so balance updates immediately in UI.
      if (selectedCustomer?.id) {
        dispatch(customerApi.util.invalidateTags([
          { type: 'Customer', id: selectedCustomer.id },
          { type: 'Customer', id: 'LIST' },
        ]));
      }
    } catch (err) {
      const code = err.data?.details?.code;
      if (code === 'prices_recalculated' || code === 'promotions_recalculated') {
        setBillUpdate({
          code,
          message: err.data?.error,
          priceChanges: err.data?.details?.price_changes || [],
          newOrder: err.data?.details?.order,
          appliedPromotions: err.data?.details?.applied_promotions || [],
          oldTotal: total,
        });
        return;
      }
      // A stale/used voucher verification rolls the whole payment back — drop the
      // voucher rows so the cashier re-scans them.
      if (gvPayments.length && /voucher/i.test(err.data?.error || '')) setGvPayments([]);
      showToast(err.data?.error || 'Payment failed. Please try again.', 'error');
    }
  };

  // The server already repriced/recalculated and persisted the order (see
  // prices_recalculated / promotions_recalculated in handleSubmit) — refresh
  // redux from that response so the screen matches what will be charged, then
  // retry with the same tenders the cashier entered.
  const handleConfirmBillUpdate = () => {
    if (!billUpdate?.newOrder) { setBillUpdate(null); return; }
    dispatch(setCurrentOrder(billUpdate.newOrder));
    dispatch(setAppliedPromotions({ applied_promotions: billUpdate.appliedPromotions, near_miss_hints: [] }));
    setBillUpdate(null);
    handleSubmit();
  };

  const handleCancelBillUpdate = () => {
    if (billUpdate?.newOrder) dispatch(setCurrentOrder(billUpdate.newOrder));
    setBillUpdate(null);
  };

  const handlePrint = async () => {
    if (!paidReceipt) return;
    if (isConnected) {
      try {
        await silentPrintOrder(paidReceipt);
        showToast('Bill printed successfully', 'success');
        return;
      } catch (err) {
        console.error('[Printer] silentPrintOrder failed:', err);
        showToast('Printer error — switching to browser print', 'warning');
      }
    } else {
      console.warn('[Printer] Not connected (status:', printerStatus, '). Using browser print.');
      showToast('Printer app not connected — printing via browser', 'info');
    }
    // Delay so the user can read the toast before the print dialog covers the screen
    await new Promise((r) => setTimeout(r, 1500));
    browserPrintOrder(paidReceipt);
  };

  // Keyboard shortcuts: Enter = Pay (or New Sale once paid), Esc = Cancel/Back,
  // P = Print receipt on the success screen.
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      const isTypingField = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';

      if (paidOrder) {
        if (e.key === 'Enter') {
          e.preventDefault();
          onNewSale?.();
        } else if ((e.key === 'p' || e.key === 'P') && paidReceipt) {
          e.preventDefault();
          handlePrint();
        }
        return;
      }

      if (isTypingField) return;

      // The bill-update confirm dialog owns Enter/Escape while it's open —
      // don't let the underlying checkout re-submit payment or close behind it.
      if (billUpdate) {
        if (e.key === 'Escape') {
          e.preventDefault();
          handleCancelBillUpdate();
        }
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }

      if (e.key === 'Enter' && canPay && !isPaying) {
        e.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, paidOrder, paidReceipt, onNewSale, onClose, canPay, isPaying, handleSubmit, handlePrint, billUpdate]);

  if (!open) return null;

  const selectedIds = new Set(payments.map(p => p.payment_mode_id));

  /* ─── Payment panel ─── */
  const PaymentPanel = (
    <Box>
      <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.07em', mb: 1.25 }}>
        PAYMENT METHOD
      </Typography>

      {/* Store Credit moved to the header tile; Loyalty panel moved below the
          payment-method block (toggled by the header Loyalty button). */}

      {/* Mode tiles — equal-size grid, adapts to any number of modes */}
      {loadingModes ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={14} sx={{ color: '#94A3B8' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>Loading…</Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          // Fixed 3 columns always — consistent 3×3 grid regardless of mode count.
          // Store config controls which modes appear via the backend API.
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0.75,
        }}>
          {modes.map((mode, modeIdx) => {
            const isWalletGatedSc = isStoreCreditMode(mode) && walletOtpEnabled;
            const scBlocked = isWalletGatedSc && (scWallet.status === 'blocked_phone' || scWallet.status === 'blocked_connector');
            const scVerifying = isWalletGatedSc && (scWallet.status === 'sending' || scWallet.status === 'pending');
            const scInvalid = isWalletGatedSc && scWallet.status === 'invalidated';
            const gvCount = isGiftVoucherMode(mode) ? gvPayments.length : 0;
            const selected = selectedIds.has(mode.id) || scVerifying || gvCount > 0;
            const disabled = (isStoreCreditMode(mode) && !selectedCustomer?.id) || scBlocked;
            return (
              <Box
                key={mode.id}
                ref={(el) => { paymentTileRefs.current[modeIdx] = el; }}
                onClick={() => !disabled && toggleMode(mode.id)}
                onKeyDown={(e) => handlePaymentTileKeyDown(e, modeIdx, mode, disabled)}
                tabIndex={0}
                role="checkbox"
                aria-checked={selected}
                aria-disabled={disabled}
                data-testid={`payment-mode-tile-${(mode.name || `mode-${mode.id}`).toLowerCase().replace(/\s+/g, '-')}`}
                sx={{
                  position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                  gap: 0.6, pl: 0.9, pr: 0.5, py: 0.7,
                  borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer',
                  border: '1.5px solid',
                  borderStyle: scVerifying ? 'dashed' : 'solid',
                  borderColor: scInvalid ? '#F59E0B' : disabled ? '#E2E8F0' : selected ? '#4F46E5' : '#E2E8F0',
                  bgcolor: scInvalid ? '#FFFBEB' : disabled ? '#F8FAFC' : selected ? '#EEF2FF' : '#F8FAFC',
                  transition: 'all 0.12s ease',
                  userSelect: 'none',
                  boxShadow: selected && !scVerifying ? '0 0 0 3px rgba(79,70,229,0.1)' : 'none',
                  opacity: disabled ? 0.55 : 1,
                  '&:hover': disabled ? {} : { borderColor: '#818CF8', bgcolor: '#F5F3FF' },
                  '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' },
                }}
              >
                {gvCount > 0 && (
                  <Box sx={{
                    position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16, px: 0.4, borderRadius: '8px',
                    bgcolor: '#DB2777', color: '#fff', fontSize: '0.58rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {gvCount}
                  </Box>
                )}
                {scWallet.status === 'verified' && isStoreCreditMode(mode) && (
                  <Box sx={{
                    position: 'absolute', top: -5, right: -5, width: 15, height: 15, borderRadius: '50%',
                    bgcolor: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CheckCircle sx={{ fontSize: 11, color: '#fff' }} />
                  </Box>
                )}
                {getModeIcon(mode.name, disabled ? '#CBD5E1' : scInvalid ? '#B45309' : selected ? '#4F46E5' : '#94A3B8', true)}
                <Typography sx={{
                  fontSize: { xs: '0.6rem', md: '0.72rem' },
                  fontWeight: 700, lineHeight: 1.25,
                  color: disabled ? '#94A3B8' : scInvalid ? '#B45309' : selected ? '#4F46E5' : '#64748B',
                  wordBreak: 'break-word',
                }}>
                  {mode.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {walletOtpEnabled && scWallet.status === 'pending' && !scWallet.modalOpen && (
        <Box
          onClick={scWallet.openModal}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, px: 1.25, py: 0.85,
            borderRadius: '8px', border: '1px solid #4F46E5', bgcolor: '#EEF2FF', cursor: 'pointer',
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4F46E5' }}>
            Code sent to customer — tap to enter
          </Typography>
        </Box>
      )}
      {walletOtpEnabled && scWallet.status === 'invalidated' && (
        <Box sx={{ mt: 1, px: 1.25, py: 0.85, borderRadius: '8px', border: '1px solid #FDE68A', bgcolor: '#FFFBEB' }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400E' }}>Order changed after verification</Typography>
          <Typography sx={{ fontSize: '0.66rem', color: '#92400E', mt: 0.25 }}>Tap Store Credit above to re-verify.</Typography>
        </Box>
      )}
      {walletOtpEnabled && scWallet.status === 'blocked_phone' && (
        <Box sx={{ mt: 1, px: 1.25, py: 0.85, borderRadius: '8px', border: '1px solid #FECACA', bgcolor: '#FEF2F2' }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#DC2626' }}>Customer has no phone number on file</Typography>
          <Typography sx={{ fontSize: '0.66rem', color: '#64748B', mt: 0.25 }}>Store Credit is unavailable for this order.</Typography>
        </Box>
      )}
      {walletOtpEnabled && scWallet.status === 'blocked_connector' && (
        <Box sx={{ mt: 1, px: 1.25, py: 0.85, borderRadius: '8px', border: '1px solid #FECACA', bgcolor: '#FEF2F2' }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#DC2626' }}>Wallet verification is not available</Typography>
          <Typography sx={{ fontSize: '0.66rem', color: '#64748B', mt: 0.25 }}>No OTP connector is configured — use a different payment mode.</Typography>
        </Box>
      )}

      {/* Hint text */}
      {payments.length === 0 && gvPayments.length === 0 && !loadingModes && (
        <Typography sx={{ fontSize: '0.7rem', color: '#CBD5E1', mt: 1, fontStyle: 'italic' }}>
          Tap a method above — tap multiple for split payment
        </Typography>
      )}

      {!loadingModes && (
        <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: payments.length === 0 ? 0.75 : 1, fontWeight: 600 }}>
          `F10` fills the remaining amount into the latest selected payment mode.
        </Typography>
      )}

      {/* Amount rows — one per selected mode, uniform height */}
      {(payments.length > 0 || gvPayments.length > 0) && (
        <Stack spacing={0.75} sx={{ mt: 1.75 }}>
          <Divider sx={{ mb: 0.75 }} />
          {payments.map((payment, paymentIdx) => {
            const mode = modes.find(m => m.id === payment.payment_mode_id);
            const paid = parseFloat(payment.amount) || 0;
            return (
              <Box
                key={payment.payment_mode_id}
                sx={{
                  display: 'flex', alignItems: 'center',
                  height: 46,
                  bgcolor: '#F8FAFC',
                  border: '1.5px solid #E2E8F0',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                {/* Mode label — fixed width, wide enough for longest names */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 0.6, px: 1.25,
                  width: 128, flexShrink: 0,
                  borderRight: '1px solid #E2E8F0',
                  height: '100%',
                  bgcolor: '#F1F5F9',
                }}>
                  {getModeIcon(mode?.name || '', '#4F46E5')}
                  <Typography sx={{
                    fontSize: '0.7rem', fontWeight: 700, color: '#4F46E5',
                    lineHeight: 1.25, wordBreak: 'break-word', textAlign: 'center',
                  }}>
                    {mode?.name || ''}
                  </Typography>
                </Box>

                {/* Amount input */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, px: 1.25, gap: 0.25 }}>
                  <CurrencyRupee sx={{ fontSize: 15, color: '#64748B', flexShrink: 0 }} />
                  <TextField
                    variant="standard"
                    placeholder="0.00"
                    type="text"
                    value={payment.amount}
                    onChange={(e) => updateAmount(payment.payment_mode_id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const next = amountFieldRefs.current[paymentIdx + 1];
                      if (next) { next.focus(); return; }
                      if (canPay && !isPaying) handleSubmit();
                    }}
                    inputRef={(el) => { amountFieldRefs.current[paymentIdx] = el; }}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{
                      'aria-label': `${mode?.name || 'Payment'} amount`,
                      inputMode: 'decimal',
                      style: { fontSize: '1rem', fontWeight: 800, color: paid > 0 ? '#1E293B' : '#94A3B8', padding: 0 },
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>

                {/* Remove button */}
                <IconButton
                  size="small"
                  onClick={() => toggleMode(payment.payment_mode_id)}
                  sx={{ mr: 0.75, p: 0.4, color: '#CBD5E1', borderRadius: '6px', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}
                >
                  <Delete sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            );
          })}
          {gvPayments.map((g) => (
            <Box key={g.voucherId} sx={{ display: 'flex', alignItems: 'center', minHeight: 46, bgcolor: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, px: 1.25, width: 128, flexShrink: 0, borderRight: '1px solid #E2E8F0', alignSelf: 'stretch', bgcolor: '#FDF2F8' }}>
                <CardGiftcard sx={{ fontSize: 16, color: '#DB2777' }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#DB2777', lineHeight: 1.25, textAlign: 'center' }}>GV ••{g.masked.slice(-4)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1, px: 1.25, minWidth: 0 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#1E293B', lineHeight: 1.2 }}>{fmt(g.amount)}</Typography>
                <Typography noWrap sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94A3B8' }}>bal after {fmt(g.balanceAfter)} · {g.typeName}</Typography>
              </Box>
              <IconButton
                size="small"
                aria-label={`Remove voucher ${g.masked}`}
                onClick={() => setGvPayments((prev) => prev.filter((x) => x.voucherId !== g.voucherId))}
                sx={{ mr: 0.75, p: 0.4, color: '#CBD5E1', borderRadius: '6px', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}
              >
                <Delete sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {gvPayments.length > 0 && (
        <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 0.75 }}>
          Vouchers can be combined with other modes. No change is given on vouchers.
        </Typography>
      )}

      {/* Status pill — only show when something has been entered */}
      {totalPaid > 0 && (
        <Box sx={{
          mt: 1.25, px: 1.5, py: 0.9, borderRadius: '8px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          bgcolor: changeDue > 0 ? '#F0FDF4' : remaining > 0 ? '#FEF2F2' : '#F0FDF4',
          border: '1.5px solid',
          borderColor: changeDue > 0 ? '#BBF7D0' : remaining > 0 ? '#FECACA' : '#BBF7D0',
        }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.04em', color: changeDue > 0 ? '#16A34A' : remaining > 0 ? '#DC2626' : '#16A34A' }}>
            {changeDue > 0 ? 'CHANGE DUE' : remaining > 0 ? 'REMAINING' : '✓ FULLY PAID'}
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: changeDue > 0 ? '#16A34A' : remaining > 0 ? '#DC2626' : '#16A34A' }}>
            {changeDue > 0 ? fmt(changeDue) : remaining > 0 ? fmt(remaining) : ''}
          </Typography>
        </Box>
      )}

      {/* Loyalty redemption — separate tender, toggled from the header button */}
      {customerDetail?.loyalty && (
        <Collapse in={loyaltyOpen} unmountOnExit>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #E2E8F0' }}>
            <CheckoutLoyaltyPanel
              loyalty={customerDetail?.loyalty}
              customerId={selectedCustomer?.id}
              isLoadingCustomer={isCustomerDetailLoading}
              onClose={() => setLoyaltyOpen(false)}
              walletOtpEnabled={walletOtpEnabled}
              walletOtpMode={walletOtpMode}
              wallet={lpWallet}
              otherWalletVerified={scWallet.status === 'verified'}
            />
          </Box>
        </Collapse>
      )}

      {walletOtpEnabled && (
        <CustomerWalletOtpModal
          open={scWallet.status === 'pending' && scWallet.modalOpen}
          onClose={scWallet.closeModal}
          title="Verify Customer — Store Credit"
          secondsLeft={scWallet.secondsLeft}
          resendCooldown={scWallet.resendCooldown}
          isResending={scWallet.isResending}
          isVerifying={scWallet.isVerifying}
          onVerify={scWallet.verify}
          onResend={scWallet.resend}
        />
      )}
    </Box>
  );

  /* ─── Action buttons ─── */
  const ActionButtons = ({ compact }) => (
    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined" onClick={onClose}
        sx={{
          flex: 1, py: compact ? 0.9 : 1.1, borderRadius: '9px',
          fontWeight: 700, fontSize: compact ? '0.8rem' : '0.85rem',
          textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B',
          '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained" disableElevation onClick={handleSubmit}
        disabled={!canPay || isPaying}
        sx={{
          flex: 2, py: compact ? 0.9 : 1.1, borderRadius: '9px',
          fontWeight: 800, fontSize: compact ? '0.88rem' : '0.92rem',
          textTransform: 'none',
          bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' },
          '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
        }}
      >
        {isPaying ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `Pay ${fmt(payable)}`}
      </Button>
    </Stack>
  );

  /* ═══════════════════════ RENDER ═══════════════════════ */
  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1200,
      height: '100dvh',
      bgcolor: '#F1F5F9',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── Top bar ── */}
      <Box sx={{
        bgcolor: '#fff', borderBottom: '1px solid #E2E8F0',
        px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.1, sm: 1.4 },
        display: 'flex', alignItems: 'center', gap: 1.25,
        flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <IconButton size="small" onClick={onClose}
          sx={{ color: '#64748B', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.55 }}>
          <ArrowBack sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#1E293B', lineHeight: 1.2 }}>
            Checkout
          </Typography>
          {(orderNumber || referenceNumber) && (
            <Typography sx={{ fontSize: '0.67rem', color: '#94A3B8', fontWeight: 500 }}>{orderNumber || referenceNumber}</Typography>
          )}
        </Box>
        {/* Header button — Apply Coupon (desktop only; mobile lives by the billing heading) */}
        <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
          <CouponTriggerButton
            open={couponOpen}
            onToggle={() => setCouponOpen((v) => !v)}
            applied={Boolean(coupon_code)}
          />
        </Box>

        {/* Header tile — Store Credit (display-only) */}
        {selectedCustomer && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
            px: { xs: 0.75, sm: 1 }, py: 0.4, borderRadius: '8px',
            border: '1px solid #E2E8F0', bgcolor: '#F8FAFC',
          }}>
            <AccountBalanceWallet sx={{ fontSize: 15, color: '#4F46E5' }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: '0.62rem', fontWeight: 700, color: '#94A3B8' }}>
              Store Credit
            </Box>
            <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#4F46E5' }}>
              {isCustomerDetailLoading ? '…' : fmt(storeCreditBalance)}
            </Typography>
          </Box>
        )}

        {/* Header button — Loyalty (toggles the redemption panel) */}
        {customerDetail?.loyalty && (
          <Badge color="warning" variant="dot" overlap="circular" invisible={redemptionAmount <= 0}>
            <Button
              size="small"
              onClick={() => setLoyaltyOpen((v) => !v)}
              startIcon={<Stars sx={{ fontSize: '15px !important', color: '#F59E0B' }} />}
              sx={{
                minWidth: 0, flexShrink: 0, px: { xs: 0.75, sm: 1.25 }, py: 0.4, borderRadius: '8px',
                textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                color: loyaltyOpen ? '#92400E' : '#64748B',
                border: '1px solid', borderColor: loyaltyOpen ? '#FCD34D' : '#E2E8F0',
                bgcolor: loyaltyOpen ? '#FFFBEB' : '#FFF',
                '&:hover': { bgcolor: '#FFFBEB', borderColor: '#FCD34D' },
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Loyalty
              </Box>
            </Button>
          </Badge>
        )}

        {selectedCustomer && (
          <Chip
            icon={<Person sx={{ fontSize: '13px !important', color: '#6366F1 !important' }} />}
            label={selectedCustomer.name}
            size="small"
            sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5', flexShrink: 0, maxWidth: { xs: 110, sm: 200 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          />
        )}
      </Box>

      {/* Sales Incentives: reminder for untagged incentive items (display only; Pay is never affected) */}
      {!paidOrder && <UntaggedIncentiveStrip onTag={onTagSalesperson} />}

      {/* ══════════════════════════════════════
          DESKTOP LAYOUT (md+)
      ══════════════════════════════════════ */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1, overflow: 'hidden', gap: 2, p: 2 }}>

        {/* LEFT — Order items + Billing */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ borderRadius: '12px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1, flexShrink: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.07em' }}>ORDER ITEMS</Typography>
                <Chip label={`${items.length} item${items.length !== 1 ? 's' : ''}`} size="small"
                  sx={{ height: 18, fontSize: '0.63rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5' }} />
              </Stack>
            </Box>
            <Divider />
            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2.5 }}>
              <ItemsList items={items} />
            </Box>
          </Paper>

          <Collapse in={couponOpen || Boolean(coupon_code)} unmountOnExit sx={{ flexShrink: 0 }}>
            <Paper variant="outlined" sx={{ borderRadius: '12px', px: 2.5, py: 1.5 }}>
              <CouponSection onApplied={() => setCouponOpen(false)} />
            </Paper>
          </Collapse>

          <Paper variant="outlined" sx={{ borderRadius: '12px', px: 2.5, py: 1.5, flexShrink: 0 }}>
            <BillingCard gross_subtotal={gross_subtotal} subtotal={subtotal} discount_total={discount_total} coupon_discount_amount={coupon_discount_amount} tax_total={tax_total} total={total} rounding_adjustment={rounding_adjustment} isIntrastate={isIntrastate} loyaltyRedemption={redemptionAmount} netPayable={payable} />
          </Paper>
        </Box>

        {/* RIGHT — Payment + actions */}
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, minHeight: 0, borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2.5, pt: 2.25, pb: 1 }}>
            {PaymentPanel}
          </Box>
          <Box sx={{ px: 2, py: 2, borderTop: '1px solid #F1F5F9', flexShrink: 0 }}>
            <ActionButtons />
          </Box>
        </Paper>
      </Box>

      {/* ══════════════════════════════════════
          MOBILE LAYOUT (xs/sm)
      ══════════════════════════════════════ */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 1.5, py: 1.5 }}>
          <Stack spacing={1.5}>

            {/* 1. Order items — now first (above payment) */}
            <Paper variant="outlined" sx={{ borderRadius: '10px', overflow: 'hidden' }}>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.75 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.07em' }}>ORDER ITEMS</Typography>
                  <Chip label={`${items.length} item${items.length !== 1 ? 's' : ''}`} size="small"
                    sx={{ height: 18, fontSize: '0.63rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5' }} />
                </Stack>
                <Divider sx={{ mb: 0.5 }} />
                <ItemsList items={items} />
              </Box>
            </Paper>

            {/* 2. Payment method */}
            <Paper variant="outlined" sx={{ borderRadius: '10px', px: 2, py: 1.5 }}>
              {PaymentPanel}
            </Paper>

            {/* 3. Billing details — coupon trigger + collapsible field live by its heading */}
            <Paper variant="outlined" sx={{ borderRadius: '10px', px: 2, py: 1.5 }}>
              <BillingCard
                gross_subtotal={gross_subtotal} subtotal={subtotal} discount_total={discount_total} coupon_discount_amount={coupon_discount_amount} tax_total={tax_total} total={total} rounding_adjustment={rounding_adjustment} isIntrastate={isIntrastate} loyaltyRedemption={redemptionAmount} netPayable={payable} compact
                headerAction={
                  <CouponTriggerButton mobile open={couponOpen} onToggle={() => setCouponOpen((v) => !v)} applied={Boolean(coupon_code)} />
                }
                couponSlot={
                  <Collapse in={couponOpen || Boolean(coupon_code)} unmountOnExit>
                    <Box sx={{ mb: 1 }}>
                      <CouponSection onApplied={() => setCouponOpen(false)} />
                    </Box>
                  </Collapse>
                }
              />
            </Paper>

          </Stack>
        </Box>

        <Box sx={{ px: 1.5, py: 1.25, bgcolor: '#fff', borderTop: '1px solid #E2E8F0', flexShrink: 0 }}>
          <ActionButtons compact />
        </Box>
      </Box>

      {/* ── Success overlay ── */}
      {paidOrder && (
        <SuccessOverlay
          order={paidOrder}
          receipt={paidReceipt}
          isReceiptLoading={isReceiptLoading}
          onNewSale={onNewSale}
          onPrint={handlePrint}
          printerStatus={printerStatus}
          customerWhatsappOptIn={paidCustomerWhatsappOptIn}
        />
      )}

      <GiftVoucherLookupDialog
        open={gvDialogOpen}
        mode="apply"
        orderId={currentOrderId}
        amountDue={remaining}
        alreadyApplied={gvPayments.map((g) => g.voucherId)}
        onClose={() => setGvDialogOpen(false)}
        onApply={(g) => {
          setGvPayments((prev) => [...prev, g]);
          setGvDialogOpen(false);
          showToast(`Voucher applied · ${fmt(g.amount)}`, 'success');
        }}
      />
      <BillUpdateConfirmDialog
        open={Boolean(billUpdate)}
        billUpdate={billUpdate}
        onConfirm={handleConfirmBillUpdate}
        onCancel={handleCancelBillUpdate}
        loading={isPaying}
      />
    </Box>
  );
};

export default CheckoutPage;
